<template>
    <div class="flex items-center">
        <div class="relative inline-flex shadow-sm rounded-md">
            <button
                :id="`cart-item-${cartItem.id}-previous-subscription-option`"
                @click="previousSubscriptionOption(cartItem, shippingTimeId)"
                type="button"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none"
            >
                <span class="sr-only">Previous Subscription Option</span>
                <MinusIcon class="h-5 w-5" aria-hidden="true" />
            </button>

            <Listbox
                class="-ml-px"
                as="div"
                v-model="selectedSubscriptionOptionId"
            >
                <ListboxLabel />
                <div class="relative">
                    <ListboxButton
                        class="bg-white relative w-full border border-gray-300 px-6 py-2 text-center cursor-default focus:outline-none font-semibold text-sm text-gray-700"
                    >
                        <span class="block"
                            >Every
                            {{ cartItem.subscription_option.how_often }}
                            Days</span
                        >
                    </ListboxButton>

                    <transition
                        leave-active-class="transition ease-in duration-100"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <ListboxOptions
                            class="absolute z-20 mt-1 w-full bg-white border border-gray-300 shadow-lg max-h-36 rounded-md py-1 overflow-y-auto overflow-x-hidden focus:outline-none text-sm"
                        >
                            <ListboxOption
                                as="template"
                                v-for="subscriptionOption in cartItem
                                    .product_option.subscription_options"
                                :key="subscriptionOption.id"
                                :value="subscriptionOption.id"
                                v-slot="{ active, selected }"
                            >
                                <li
                                    :class="[
                                        active
                                            ? 'text-white bg-nutreance-green'
                                            : 'text-gray-700',
                                        'cursor-default select-none py-2 px-2 flex justify-between items-center space-x-1',
                                    ]"
                                >
                                    <span
                                        :class="[
                                            selected
                                                ? 'font-semibold'
                                                : 'font-normal',
                                        ]"
                                    >
                                        {{
                                            subscriptionOption.how_often
                                                ? `Every ${subscriptionOption.how_often} Days`
                                                : "No Auto-Delivery"
                                        }}
                                    </span>

                                    <span
                                        v-if="selected"
                                        :class="[
                                            active
                                                ? 'text-white'
                                                : 'text-nutreance-green',
                                        ]"
                                    >
                                        <CheckIcon
                                            class="h-5 w-5"
                                            aria-hidden="true"
                                        />
                                    </span>
                                </li>
                            </ListboxOption>
                        </ListboxOptions>
                    </transition>
                </div>
            </Listbox>

            <button
                :id="`cart-item-${cartItem.id}-next-subscription-option`"
                @click="nextSubscriptionOption(cartItem, shippingTimeId)"
                type="button"
                :class="[
                    nextSubscriptionOptionExists(cartItem)
                        ? 'bg-white hover:bg-gray-50 text-gray-500'
                        : 'bg-gray-100 text-gray-300 cursor-not-allowed',
                    '-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium focus:z-10 focus:outline-none',
                ]"
            >
                <span class="sr-only">Next Subscription Option</span>
                <PlusIcon class="h-5 w-5" aria-hidden="true" />
            </button>
        </div>

        <div
            v-if="loading"
            class="ml-3 flex items-center text-xs font-medium text-gray-400 hover:text-gray-500 focus:outline-none"
        >
            <LoadingIcon class="h-5 w-5 text-gray-500" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { CheckIcon, MinusIcon, PlusIcon } from "@heroicons/vue/solid";
import {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
} from "@headlessui/vue";
import { useCartItem } from "../../functions/useCartItem";
import LoadingIcon from "../LoadingIcon.vue";

const props = defineProps<{
    cartItem?: any;
    shippingTimeId?: number;
}>();

const {
    loading,
    previousSubscriptionOption,
    nextSubscriptionOption,
    nextSubscriptionOptionExists,
    updateSubscriptionOption,
} = useCartItem();
const selectedSubscriptionOptionId = ref(props.cartItem.subscription_option_id);

watch(
    () => selectedSubscriptionOptionId.value,
    (subscriptionOptionId) => {
        updateSubscriptionOption(
            props.cartItem,
            subscriptionOptionId,
            props.shippingTimeId,
        );
    },
);

watch(
    () => props.cartItem.subscription_option_id,
    (subscriptionOptionId) => {
        selectedSubscriptionOptionId.value = subscriptionOptionId;
    },
);
</script>
